// "Delta Net Impairment" - third section of "IC Reporting", ported from
// reporting-hub/frontend/src/components/preflight/report/
// {DeltaNetImpairmentCard,DeltaNetImpairmentChart}.tsx. Monthly
// net_impairment level (EAD not covered by expected recovery) for the fund
// or a single platform, plus its month-over-month delta. Like Deployment,
// always recomputed fresh from raw LoanTape/CashFlows - works before
// "Confirm & Calculate". Fixed monthly grain, no frequency param. Self
// contained like InvestmentRatioCard (owns `reportingId` only), but owns its
// own Fund/platform scope state locally (a time-series chart, not a
// snapshot table).
//
// Bar (net_impairment level) + line (month-over-month delta) combo on one
// shared y-axis (both series are the same unit, EUR) - chart.js mixed
// bar/line datasets, same pattern as LendReportSections.js's
// DefaultRateReturnChart/ProjectedCashflowChart (those use a second axis
// since their two series are different units; this one doesn't need to).

import React, { useEffect, useMemo, useState } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { Chart } from 'primereact/chart';
import { SelectButton } from 'primereact/selectbutton';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { BASE_OPTIONS, chartRemountKey } from '../../reporting/chart_theme';
import { formatCurrency, formatDate, titleCasePlatform, PLATFORM_ORDER } from '../calculation/calcFormat';
import errorMessage from '../../../helpers/errorMessage';

const LEVEL_COLOR = '#0d366b';
const DELTA_COLOR = '#eda100';

function formatCurrencyCompact(value) {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    const millions = abs / 1_000_000;
    return `${sign}${(millions >= 10 ? millions.toFixed(0) : millions.toFixed(1)).replace(/\.0$/, '')}M`;
  }
  if (abs >= 1_000) {
    const thousands = abs / 1_000;
    return `${sign}${(thousands >= 10 ? thousands.toFixed(0) : thousands.toFixed(1)).replace(/\.0$/, '')}K`;
  }
  return `${sign}${abs.toFixed(0)}`;
}

function DeltaNetImpairmentChart({ data }) {
  const chartData = useMemo(
    () => ({
      labels: data.buckets.map((b) => b.label),
      datasets: [
        { type: 'bar', label: 'Net Impairment', backgroundColor: LEVEL_COLOR, data: data.buckets.map((b) => b.net_impairment) },
        {
          type: 'line',
          label: 'Δ vs. prior month',
          borderColor: DELTA_COLOR,
          backgroundColor: DELTA_COLOR,
          data: data.buckets.map((b) => b.delta_net_impairment),
          fill: false,
          pointRadius: 3,
        },
      ],
    }),
    [data]
  );

  const options = useMemo(
    () => ({
      ...BASE_OPTIONS,
      scales: { y: { ticks: { callback: (v) => formatCurrencyCompact(v) } } },
      plugins: {
        legend: { position: 'bottom', labels: { boxWidth: 12 } },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (items) => {
              const b = data.buckets[items[0]?.dataIndex ?? 0];
              return b ? `${b.label} (${formatDate(b.start)} - ${formatDate(b.end)})` : '';
            },
            label: (item) => `${item.dataset.label}: ${formatCurrency(item.raw)}`,
          },
        },
      },
    }),
    [data]
  );

  return (
    <div style={{ height: 340 }}>
      <Chart key={chartRemountKey(chartData)} type="bar" data={chartData} options={options} style={{ height: '100%', width: '100%' }} />
    </div>
  );
}

const DeltaNetImpairmentCard = ({ reportingId }) => {
  const [scope, setScope] = useState('fund');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!reportingId) return;
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (scope !== 'fund') params.platform = scope;
    axiosWithAuth
      .get(`/compartment-report/reportings/${reportingId}/delta-net-impairment`, { params })
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[DeltaNetImpairmentCard] fetch error:', errorMessage(err));
          setError(errorMessage(err, 'Failed to load Delta Net Impairment.'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportingId, scope]);

  return (
    <div className="cr-card cr-report-card">
      <div className="flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div>
          <h4 className="cr-card-title cr-card-title-block">Delta Net Impairment</h4>
          <p className="cr-card-subtitle mb-0">
            Net impairment (EAD not covered by expected recovery) at each month-end, and how much it changed since the prior month.
          </p>
        </div>
        <SelectButton
          value={scope}
          options={[{ label: 'Fund', value: 'fund' }, ...PLATFORM_ORDER.map((p) => ({ label: titleCasePlatform(p), value: p }))]}
          onChange={(e) => (e.value ? setScope(e.value) : null)}
        />
      </div>

      {error ? (
        <div className="ex-inline-error">{error}</div>
      ) : loading || !data ? (
        <Skeleton height="360px" borderRadius="12px" />
      ) : data.window === null || data.buckets.length === 0 ? (
        <p className="text-color-secondary">No defaulted loans found for this selection.</p>
      ) : (
        <div>
          <DeltaNetImpairmentChart data={data} />
          <DataTable value={data.buckets} size="small" showGridlines className="mt-3">
            <Column field="label" header="Month" />
            <Column header="Net Impairment" align="right" headerClassName="cr-num" bodyClassName="cr-num" body={(row) => formatCurrency(row.net_impairment)} />
            <Column header="Δ vs. prior month" align="right" headerClassName="cr-num" bodyClassName="cr-num" body={(row) => formatCurrency(row.delta_net_impairment)} />
          </DataTable>
        </div>
      )}
    </div>
  );
};

export default DeltaNetImpairmentCard;
